import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ApplicationErrorLogTable.css';
import { buildApiUrl, getApiHeaders } from './config';

const DEFAULT_FILTERS = {
  id: '',
  status: 'OPEN',
  severity: '',
  serviceName: '',
  occurrenceCount: '',
  lastSeenAt: '',
  firstSeenAt: '',
  endpoint: '',
  symbol: '',
  timeFrame: '',
  errorType: '',
  errorMessage: '',
  rootCause: '',
  correlationId: '',
  source: '',
  size: '25',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const badgeClass = (status) => `application-error-log-badge ${(status || '').toLowerCase()}`;

const matchesFilter = (value, filter) =>
  !filter || String(value ?? '').toLowerCase().includes(String(filter).trim().toLowerCase());

export default function ApplicationErrorLogTable() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError('');
    setMessage('');

    const params = {
      status: filters.status,
      severity: filters.severity,
      serviceName: filters.serviceName,
      text: '',
      page: 0,
      size: filters.size,
    };

    fetch(buildApiUrl('/api/bot/ui/observability/errors/recent', params), {
      headers: getApiHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch errors: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        setLogs(content);
        setExpandedId(null);
      })
      .catch((fetchError) => {
        console.error(fetchError);
        setError(fetchError.message || 'Unable to fetch application errors');
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, [filters.serviceName, filters.severity, filters.size, filters.status]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateStatus = (id, action) => {
    setLoading(true);
    setError('');
    setMessage('');

    fetch(buildApiUrl(`/api/bot/ui/observability/errors/${id}/${action}`, { user: 'ADMIN_UI' }), {
      method: 'POST',
      headers: getApiHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${action} failed: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        setMessage(`Error ${id} ${action}d`);
        fetchLogs();
      })
      .catch((actionError) => {
        console.error(actionError);
        setError(actionError.message || `Unable to ${action} error`);
      })
      .finally(() => setLoading(false));
  };

  const visibleLogs = useMemo(() => {
    return logs.filter((log) => {
      const source = [log.sourceClass, log.sourceMethod].filter(Boolean).join('#');
      return matchesFilter(log.id, filters.id)
        && matchesFilter(log.status, filters.status)
        && matchesFilter(log.serviceName, filters.serviceName)
        && matchesFilter(log.severity, filters.severity)
        && matchesFilter(log.occurrenceCount, filters.occurrenceCount)
        && matchesFilter(formatDate(log.lastSeenAt), filters.lastSeenAt)
        && matchesFilter(formatDate(log.firstSeenAt), filters.firstSeenAt)
        && matchesFilter(log.endpoint, filters.endpoint)
        && matchesFilter(log.symbol, filters.symbol)
        && matchesFilter(log.timeFrame, filters.timeFrame)
        && matchesFilter(log.errorType, filters.errorType)
        && matchesFilter(log.errorMessage, filters.errorMessage)
        && matchesFilter(log.rootCause, filters.rootCause)
        && matchesFilter(log.correlationId, filters.correlationId)
        && matchesFilter(source, filters.source);
    });
  }, [filters, logs]);

  return (
    <div className="application-error-log-wrapper">
      <div className="application-error-log-header">
        <div className="application-error-log-title-block">
          <h2>Application Error Logs</h2>
          <p>Unhandled API and async exceptions captured by the bot application.</p>
        </div>
        <div className="application-error-log-actions">
          <button type="button" onClick={fetchLogs} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button className="application-error-log-reset" type="button" onClick={() => setFilters(DEFAULT_FILTERS)}>
            <span className="application-error-log-reset__icon" aria-hidden="true" />
            Reset Filters
          </button>
        </div>
      </div>

      {error && <div className="application-error-log-status error">{error}</div>}
      {message && !error && <div className="application-error-log-status info">{message}</div>}

      <div className="application-error-log-table-shell">
        <table className="application-error-log-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Service</th>
              <th>Severity</th>
              <th>Occurrences</th>
              <th>Last Seen</th>
              <th>First Seen</th>
              <th>Endpoint</th>
              <th>Symbol</th>
              <th>TF</th>
              <th>Error Type</th>
              <th>Message</th>
              <th>Root Cause</th>
              <th>Correlation ID</th>
              <th>Source</th>
              <th>Actions</th>
            </tr>
            <tr className="application-error-log-filter-row">
              <th><input value={filters.id} onChange={(event) => updateFilter('id', event.target.value)} placeholder="ID" /></th>
              <th>
                <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
                  <option value="">ALL</option>
                  <option value="OPEN">OPEN</option>
                  <option value="ACKNOWLEDGED">ACKNOWLEDGED</option>
                  <option value="RESOLVED">RESOLVED</option>
                </select>
              </th>
              <th><input value={filters.serviceName} onChange={(event) => updateFilter('serviceName', event.target.value)} placeholder="Service" /></th>
              <th>
                <select value={filters.severity} onChange={(event) => updateFilter('severity', event.target.value)}>
                  <option value="">ALL</option>
                  <option value="ERROR">ERROR</option>
                  <option value="WARN">WARN</option>
                  <option value="INFO">INFO</option>
                </select>
              </th>
              <th><input value={filters.occurrenceCount} onChange={(event) => updateFilter('occurrenceCount', event.target.value)} placeholder="Count" /></th>
              <th><input value={filters.lastSeenAt} onChange={(event) => updateFilter('lastSeenAt', event.target.value)} placeholder="Last seen" /></th>
              <th><input value={filters.firstSeenAt} onChange={(event) => updateFilter('firstSeenAt', event.target.value)} placeholder="First seen" /></th>
              <th><input value={filters.endpoint} onChange={(event) => updateFilter('endpoint', event.target.value)} placeholder="Endpoint" /></th>
              <th><input value={filters.symbol} onChange={(event) => updateFilter('symbol', event.target.value)} placeholder="Symbol" /></th>
              <th><input value={filters.timeFrame} onChange={(event) => updateFilter('timeFrame', event.target.value)} placeholder="TF" /></th>
              <th><input value={filters.errorType} onChange={(event) => updateFilter('errorType', event.target.value)} placeholder="Type" /></th>
              <th><input value={filters.errorMessage} onChange={(event) => updateFilter('errorMessage', event.target.value)} placeholder="Message" /></th>
              <th><input value={filters.rootCause} onChange={(event) => updateFilter('rootCause', event.target.value)} placeholder="Root cause" /></th>
              <th><input value={filters.correlationId} onChange={(event) => updateFilter('correlationId', event.target.value)} placeholder="Correlation" /></th>
              <th><input value={filters.source} onChange={(event) => updateFilter('source', event.target.value)} placeholder="Source" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleLogs.length === 0 && (
              <tr>
                <td colSpan="16">No application errors found.</td>
              </tr>
            )}
            {visibleLogs.map((log) => (
              <React.Fragment key={log.id}>
                <tr>
                  <td>{log.id}</td>
                  <td><span className={badgeClass(log.status)}>{log.status || '-'}</span></td>
                  <td>{log.serviceName || '-'}</td>
                  <td>{log.severity || '-'}</td>
                  <td>{log.occurrenceCount ?? '-'}</td>
                  <td>{formatDate(log.lastSeenAt)}</td>
                  <td>{formatDate(log.firstSeenAt)}</td>
                  <td>{log.endpoint || '-'}</td>
                  <td>{log.symbol || '-'}</td>
                  <td>{log.timeFrame || '-'}</td>
                  <td className="application-error-log-type">{log.errorType || '-'}</td>
                  <td className="application-error-log-message">{log.errorMessage || '-'}</td>
                  <td className="application-error-log-message">{log.rootCause || '-'}</td>
                  <td className="application-error-log-correlation">{log.correlationId || '-'}</td>
                  <td className="application-error-log-source">{[log.sourceClass, log.sourceMethod].filter(Boolean).join('#') || '-'}</td>
                  <td className="application-error-log-row-actions">
                    <button
                      className="application-error-log-row-action details"
                      type="button"
                      onClick={() => setExpandedId((current) => current === log.id ? null : log.id)}
                    >
                      {expandedId === log.id ? 'Hide Details' : 'Details'}
                    </button>
                    <button
                      className="application-error-log-row-action acknowledge"
                      type="button"
                      disabled={loading || log.status === 'ACKNOWLEDGED' || log.status === 'RESOLVED'}
                      onClick={() => updateStatus(log.id, 'acknowledge')}
                    >
                      Ack
                    </button>
                    <button
                      className="application-error-log-row-action resolve"
                      type="button"
                      disabled={loading || log.status === 'RESOLVED'}
                      onClick={() => updateStatus(log.id, 'resolve')}
                    >
                      Resolve
                    </button>
                  </td>
                </tr>
                {expandedId === log.id && (
                  <tr className="application-error-log-details-row">
                    <td colSpan="16">
                      <div className="application-error-log-details">
                        <div className="application-error-log-details-grid">
                          <div>
                            <span>Request URI</span>
                            <strong>{log.requestUri || '-'}</strong>
                          </div>
                          <div>
                            <span>Query String</span>
                            <strong>{log.queryString || '-'}</strong>
                          </div>
                          <div>
                            <span>Exchange / Market</span>
                            <strong>{[log.exchange, log.marketType].filter(Boolean).join(' / ') || '-'}</strong>
                          </div>
                          <div>
                            <span>User</span>
                            <strong>{log.username || log.userId || '-'}</strong>
                          </div>
                        </div>
                        <div className="application-error-log-stack-title">Stack Trace</div>
                        <pre className="application-error-log-stack-block">{log.stackTrace || '-'}</pre>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="application-error-log-record-count">
        Showing {visibleLogs.length} of {logs.length} records
      </div>
    </div>
  );
}
